import { getQualityBand } from 'partner360/components/qualityIndicator'

import { sortFinancialPlanQualityData } from '../../../lib/sortFinancialPlanQualityData'
import {
  IFinancialPlanQuality,
  IFinancialPlanQualityCategoryScore,
  IFinancialPlanQualityData,
  IFinancialPlanQualityPillar,
  TFinancialPlanQualityValueUnit,
} from '../../../model/types'

const DEFAULT_CATEGORY_LABEL = 'Total'
const HIGH_SOB_BASELINE_THRESHOLD = 90

const isSobMandatoryOrPlanned = (row: IFinancialPlanQualityData): boolean =>
  row.isSobAvailable !== false &&
  (row.isSobMandatory === true ||
    row.sobPlan > 0 ||
    Boolean(row.children?.some(isSobMandatoryOrPlanned)))

const getPoints = (planned: number, target: number) => {
  if (planned <= 0) {
    return 0
  }

  if (target <= 0 || planned >= target) {
    return 100
  }

  return Math.round((planned / target) * 100)
}

interface IBuildCategoryScoresParams {
  finData: IFinancialPlanQualityData[]
  targetField: 'finYoyGrowth' | 'sobYoyGrowth'
  currentField: 'plan' | 'sobPlan'
  baselineField: 'baseline' | 'sobBaseline'
  valueUnit: TFinancialPlanQualityValueUnit
}

const buildCategoryScores = ({
  finData,
  targetField,
  currentField,
  baselineField,
  valueUnit,
}: IBuildCategoryScoresParams): IFinancialPlanQualityCategoryScore[] => {
  return finData.map((row) => {
    const id = row.id
    const category = row.category || DEFAULT_CATEGORY_LABEL
    const offering = row.offering
    const rawTarget = row[targetField]
    const target = typeof rawTarget === 'number' ? rawTarget : null
    const children = row.children
      ? buildCategoryScores({
          finData: row.children,
          targetField,
          currentField,
          baselineField,
          valueUnit,
        })
      : undefined
    const planningLink = offering ? undefined : row.planningLink
    const current = Number(row[currentField]) || 0
    const baseline = Number(row[baselineField]) || 0
    const absoluteChange = baseline || valueUnit === 'percentagePoints' ? current - baseline : null
    const baseScore = {
      id,
      offering,
      category,
      target,
      children,
      planningLink,
      absoluteChange,
    }

    if (!baseline && valueUnit === 'percent') {
      return {
        ...baseScore,
        planned: null,
        points: null,
        band: null,
      }
    }

    const change = current - baseline
    const planned = valueUnit === 'percent' ? (change / baseline) * 100 : change

    if (target === null) {
      return {
        ...baseScore,
        planned,
        points: null,
        band: null,
      }
    }

    if (baselineField === 'sobBaseline' && baseline >= HIGH_SOB_BASELINE_THRESHOLD) {
      const points = current >= baseline ? 100 : 0

      return {
        ...baseScore,
        planned: change,
        points,
        band: getQualityBand(points),
      }
    }

    const points = getPoints(planned, target)

    return {
      ...baseScore,
      planned,
      points,
      band: getQualityBand(points),
    }
  })
}

const groupByOffering = (
  categories: IFinancialPlanQualityCategoryScore[],
  offerings: string[]
): IFinancialPlanQualityCategoryScore[] => {
  const groups = new Map<string, IFinancialPlanQualityCategoryScore>()
  const result: IFinancialPlanQualityCategoryScore[] = []

  const addOffering = (offering: string) => {
    const existingGroup = groups.get(offering)
    if (existingGroup) {
      return existingGroup
    }

    const group: IFinancialPlanQualityCategoryScore = {
      id: `plan-quality-offering\0${offering}`,
      category: offering,
      planned: null,
      absoluteChange: null,
      target: null,
      points: null,
      band: null,
      children: [],
    }
    groups.set(offering, group)
    result.push(group)
    return group
  }

  offerings.forEach(addOffering)
  categories.forEach((category) => {
    if (!category.offering) {
      result.push(category)
      return
    }

    const group = addOffering(category.offering)
    group.children!.push({ ...category, offering: undefined })
  })

  return result
}

interface IGetPillarParams {
  offerings?: string[]
  categories: IFinancialPlanQualityCategoryScore[]
  valueUnit: TFinancialPlanQualityValueUnit
}

const getPillar = ({
  categories,
  valueUnit,
  offerings = [],
}: IGetPillarParams): IFinancialPlanQualityPillar => {
  const scoredPoints = categories
    .map(({ points }) => points)
    .filter((points): points is number => points !== null)

  const score = scoredPoints.length
    ? Math.round(scoredPoints.reduce((sum, points) => sum + points, 0) / scoredPoints.length)
    : 0

  return {
    score,
    band: getQualityBand(score),
    categories: groupByOffering(categories, offerings),
    valueUnit,
  }
}

export const getFinancialPlanQuality = (
  finData: IFinancialPlanQualityData[]
): IFinancialPlanQuality => {
  const isOverall = finData.some(({ offering }) => Boolean(offering))
  const allSortedFinData = sortFinancialPlanQualityData(finData)
  const getOfferings = (rows: IFinancialPlanQualityData[]) => [
    ...new Set(rows.flatMap(({ offering }) => (offering ? [offering] : []))),
  ]
  const sortedFinData = isOverall
    ? allSortedFinData.filter(({ plan }) => plan > 0)
    : allSortedFinData
  const financialFinData = sortedFinData
    .filter(({ isFinancialApplicable }) => isFinancialApplicable !== false)
    .map((row) => ({
      ...row,
      children: row.children?.filter(({ plan }) => plan > 0),
    }))
  const sobFinData = allSortedFinData.filter(isSobMandatoryOrPlanned).map((row) => ({
    ...row,
    children: row.children?.filter(isSobMandatoryOrPlanned),
  }))
  const financialGrowth = getPillar({
    offerings: getOfferings(allSortedFinData),
    categories: buildCategoryScores({
      finData: financialFinData,
      targetField: 'finYoyGrowth',
      currentField: 'plan',
      baselineField: 'baseline',
      valueUnit: 'percent',
    }),
    valueUnit: 'percent',
  })

  const sobGrowth = getPillar({
    offerings: getOfferings(
      allSortedFinData.filter(({ isSobAvailable }) => isSobAvailable !== false)
    ),
    categories: buildCategoryScores({
      finData: sobFinData,
      targetField: 'sobYoyGrowth',
      currentField: 'sobPlan',
      baselineField: 'sobBaseline',
      valueUnit: 'percentagePoints',
    }),
    valueUnit: 'percentagePoints',
  })

  const overallScore = sobFinData.length
    ? Math.round((financialGrowth.score + sobGrowth.score) / 2)
    : financialGrowth.score

  return {
    overall: {
      score: overallScore,
      band: getQualityBand(overallScore),
    },
    financialGrowth,
    sobGrowth,
  }
}
